import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import type { AlertsState, ExportAlertsPayload } from "../types/alerts.types";
import { ls } from "@/shared/utils/localStorage";
import { alertService } from "../services/alertService";
import type { RootState } from "../../../store/store";
import { debounce } from "@/shared/utils/debounce";
import { commonService } from "@/services/commonService";
import type { AppDispatch } from "@/store/store";
import type { CustomerProjectEnv } from "@/shared/types/common.types";

// Assets
import HxGNConnect from "../../../assets/images/hxgn-connect.png";
import HxGNOnCall from "../../../assets/images/hxgn-oncall.png";

const initialState: AlertsState = {
  AlertsLoadDst: [],
  ProjectsDst: [],
  CustomersProjectEnvDst: [],
  _lastRefreshedTimeDst: null,
  status: {
    AlertsLoadDst: "idle",
    ProjectsDst: "idle",
    CustomersProjectEnvDst: "idle",
  },
  filters: {
    customer: ls.get<string[]>("SelectedAlertsCustomers", []),
    severity: ls.get<string[]>("AlertsSeverity", ["Critical"]),
    project: [],
    environment: [],
  },
  filtersInitialised: true,
};

/// Alerts Data Load
export const fetchMasterAlerts = createAsyncThunk(
  "alertStore/fetchMasterAlerts",
  async (payload: ExportAlertsPayload, { signal, rejectWithValue }) => {
    try {
      const res = await alertService.GetAllAlerts(payload, { signal });
      return res;
    } catch (err: unknown) {
      const errorName =
        typeof err === "object" && err !== null && "name" in err
          ? String((err as { name?: unknown }).name ?? "")
          : "";

      if (errorName === "AbortError" || errorName === "CanceledError") {
        return rejectWithValue("aborted");
      }
      throw err;
    }
  },
);

export const fetchMasterProjects = createAsyncThunk(
  "alertStore/fetchMasterProjects",
  async () => {
    const res = await commonService.GetProjects();
    return res.map((proj) => ({
      ...proj,
      icon: proj.projectName === "HxGN Connect" ? HxGNConnect : HxGNOnCall,
    }));
  },
);

export const fetchCustomersProjectEnv = createAsyncThunk(
  "alertStore/fetchCustomersProjectEnv",
  async () => {
    return await commonService.GetCustomersProjectEnv();
  },
);

// ------ Slice -------

const alertSlice = createSlice({
  name: "alert",
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters(state) {
      state.filters = {
        customer: [],
        severity: [],
        project: [],
        environment: [],
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterAlerts.pending, (state) => {
        state.status.AlertsLoadDst = "loading";
      })
      .addCase(fetchMasterAlerts.fulfilled, (state, action) => {
        state.AlertsLoadDst = action.payload;
        state.status.AlertsLoadDst = "succeeded";
        state.filtersInitialised = true;
      })
      .addCase(fetchMasterAlerts.rejected, (state) => {
        state.status.AlertsLoadDst = "failed";
      })
      .addCase(fetchMasterProjects.fulfilled, (state, action) => {
        state.ProjectsDst = action.payload;
        state.status.ProjectsDst = "succeeded";
      })
      .addCase(fetchMasterProjects.rejected, (state) => {
        state.status.ProjectsDst = "failed";
      })
      .addCase(fetchCustomersProjectEnv.pending, (state) => {
        state.status.CustomersProjectEnvDst = "loading";
      })
      .addCase(fetchCustomersProjectEnv.fulfilled, (state, action) => {
        state.CustomersProjectEnvDst = action.payload;
        state.status.CustomersProjectEnvDst = "succeeded";
      })
      .addCase(fetchCustomersProjectEnv.rejected, (state) => {
        state.status.CustomersProjectEnvDst = "failed";
      });
  },
});

/// --- Debounced severity filter dispatcher ---
let debouncedDispatch: ((sev: string[], dispatch: AppDispatch) => void) | null = null;

export const setSeverityFiltersDebounced =
  (sev: string[]) => (dispatch: AppDispatch) => {
    if (!debouncedDispatch) {
      debouncedDispatch = debounce((s: string[], d: AppDispatch) => {
        d(setFilters({ severity: s }));
      }, 100);
    }
    debouncedDispatch(sev, dispatch);
  };

// --- Selectors ---
export const selectFilters = (state: RootState) => state.alerts.filters;
export const selectAlertsRaw = (state: RootState) =>
  state.alerts.AlertsLoadDst;
export const selectAlertsLoadStatus = (state: RootState) =>
  state.alerts.status.AlertsLoadDst;
export const selectProjects = (state: RootState) =>
  state.alerts.ProjectsDst;

export const selectFiltersInitialised = (state: RootState) =>
  state.alerts.filtersInitialised;

// --- Alerts Filtering ---
export const selectFilteredAlerts = createSelector(
  [selectAlertsRaw, selectFilters, selectProjects, selectFiltersInitialised],
  (alerts, filters, projects, initialised) => {
    if (!initialised) return [];

    const allProjects = projects.map((p) => p.projectName?.toLowerCase());
    let filtered = alerts;

    // Project filter
    if (
      filters.project.length > 0 &&
      filters.project.length !== allProjects.length
    ) {
      filtered = filtered.filter((a) =>
        filters.project.some(
          (p) => p.toLowerCase() === (a.project ?? "").toLowerCase(),
        ),
      );
    }

    // Environment filter
    if (filters.environment?.length > 0) {
      filtered = filtered.filter((a) =>
        filters.environment.includes(a.environment ?? ""),
      );
    }

    // Customer filter
    if (filters.customer.length > 0) {
      filtered = filtered.filter((a) =>
        filters.customer.includes(a.customer ?? ""),
      );
    }

    // Severity filter
    if (filters.severity.length > 0) {
      filtered = filtered.filter((a) => {
        const sev = a.severity?.trim().toLowerCase();
        const filterSeverities = filters.severity.map((s) => s.toLowerCase());
        return (
          filterSeverities.includes(sev ?? "") ||
          (filterSeverities.includes("informational") &&
            (sev === "informational" || sev === "verbose"))
        );
      });
    }

    return filtered;
  },
);

// --- Alerts for Severity Counts ---
export const selectAlertsForSeverityCounts = createSelector(
  [selectAlertsRaw, selectFilters],
  (alerts, filters) => {
    return alerts.filter((alert) => {
      let match = true;
      if (filters.customer.length > 0)
        match = match && filters.customer.includes(alert.customer ?? "");
      if (filters.project.length > 0)
        match =
          match &&
          filters.project.some(
            (p) => p.toLowerCase() === (alert.project ?? "").toLowerCase(),
          );
      if (filters.environment.length > 0)
        match =
          match &&
          filters.environment.some(
            (p) => p.toLowerCase() === (alert.environment ?? "").toLowerCase(),
          );
      return match;
    });
  },
);

// --- Available Customers ---
export const selectAvailableCustomers = createSelector(
  [selectAlertsRaw, selectFilters, selectProjects],
  (alerts, filters, projects) => {
    const allProjects = projects.map((p) => p.projectName);
    let filtered = alerts;

    if (
      filters.project.length > 0 &&
      filters.project.length !== allProjects.length
    ) {
      filtered = filtered.filter((a) =>
        filters.project.some(
          (p) => p.toLowerCase() === (a.project ?? "").toLowerCase(),
        ),
      );
    }

    return [...new Set(filtered.map((a) => a.customer ?? ""))];
  },
);

// --- Available Projects ---
export const selectAvailableProjects = createSelector(
  [selectAlertsRaw, selectFilters],
  (alerts, filters) => {
    let filtered = alerts;

    if (filters.customer.length > 0) {
      filtered = filtered.filter((a) =>
        filters.customer.includes(a.customer ?? ""),
      );
    }

    return [...new Set(filtered.map((a) => a.project ?? ""))];
  },
);

// --- All Environments ---
export const selectAllEnvironments = createSelector(
  [(state: RootState) => state.alerts.CustomersProjectEnvDst],
  (customerProjEnvs) => {
    return [
      ...new Set(
        customerProjEnvs.map((c: CustomerProjectEnv) => c.environment),
      ),
    ].filter(Boolean);
  },
);

export const { setFilters, clearFilters } = alertSlice.actions;
export default alertSlice.reducer;